import { Head, Link, usePage } from "@inertiajs/react"
import {
  NativeNavbar,
  NativeButton,
  NativeMenuItem,
  NativeFab,
} from "@ruby-native/react"
import { Download, Plus } from "lucide-react"

import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import {
  newRecipeImportPath,
  newRecipePath,
  recipePath,
  recipesPath,
} from "@/routes"
import type { BreadcrumbItem, Recipe } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Recipes",
    href: recipesPath(),
  },
]

interface Props {
  recipes: Pick<
    Recipe,
    "id" | "slug" | "title" | "description" | "cover_image_url" | "updated_at"
  >[]
}

export default function Index({ recipes }: Props) {
  const { nativeApp } = usePage().props
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <NativeNavbar title="Recipes">
        <NativeButton position="trailing" icon="plus">
          <NativeMenuItem
            title="Import Recipe"
            href="/recipe_imports/new"
            icon="arrow.down"
          />
          <NativeMenuItem title="Add Recipe" href="/recipes/new" icon="plus" />
        </NativeButton>
      </NativeNavbar>
      <NativeFab icon="plus" href={newRecipePath()} />
      <Head title="Recipes" />
      <div className="p-4">
        {!nativeApp && (
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-2xl tracking-tight">Recipes</h1>
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link href={newRecipeImportPath()}>
                  <Download className="mr-2 h-4 w-4" />
                  Import Recipe
                </Link>
              </Button>
              <Button asChild>
                <Link href={newRecipePath()}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Recipe
                </Link>
              </Button>
            </div>
          </div>
        )}

        {recipes.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12">
            <p className="text-muted-foreground mb-4 text-lg">No recipes yet</p>
            <Button asChild>
              <Link href={newRecipePath()}>Create your first recipe</Link>
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {recipes.map((recipe) => (
              <Link
                key={recipe.id}
                href={recipePath({ id: recipe.slug })}
                className="bg-card group overflow-hidden rounded-lg border transition-shadow hover:shadow-md"
              >
                <div className="bg-muted aspect-video">
                  {recipe.cover_image_url ? (
                    <img
                      src={recipe.cover_image_url}
                      alt={recipe.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <span className="text-muted-foreground text-4xl">
                        {"\u{1F37D}\uFE0F"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h2 className="text-lg group-hover:underline">
                    {recipe.title}
                  </h2>
                  {recipe.description && (
                    <p className="text-muted-foreground mt-1 line-clamp-2 text-sm">
                      {recipe.description}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
